import {useEffect, useMemo, useRef} from "react";
import {useRecoilValue} from "recoil";
import {ComplectationBlock, PreviewSection, PreviewReferencesCollection, OptionsSelected} from "../../data";
import {stateSelectedAccessories, stateSelectedComplectations} from "../../data/atoms";
import {findBlockItemBySelected, generateUid} from "../../utils/ApplicationUtils";
import {waitForSvgAndColorize, waitForSvgAndDecolorize} from "../../utils/ColorizeUtils";


export const useResultPreviewSections = (complectations: ComplectationBlock[], accessories: ComplectationBlock[]) => {
    const references = useRef<PreviewReferencesCollection>({});

    const chosenComplectations = useRecoilValue(stateSelectedComplectations);
    const chosenAccessories = useRecoilValue(stateSelectedAccessories);

    const sectionsComplectations = useSections(complectations, chosenComplectations);
    const sectionsAccessories = useSections(accessories, chosenAccessories);

    const sectionsAccessoriesFiltered = useMemo(
        () => sectionsAccessories.filter(section => chosenAccessories.hasOwnProperty(section.uid)),
        [chosenAccessories, sectionsAccessories]
    );


    useEffect(() => {
        const selected = {
            ...chosenComplectations,
            ...chosenAccessories,
        };

        /**
         * Append render for selected elements
         *
         * All elements that were selected should be rendered
         */
        for (const uid in selected) {
            const texture = selected[uid]?.imageFill || null;

            if (!selected[uid]) {
                waitForSvgAndDecolorize(references, uid);
            } else {
                waitForSvgAndColorize(references, uid, texture);
            }
        }


        /**
         * Clear render for unselected elements
         *
         * For expample pins can be unselected, so should be removed from view
         */
        for (const referenceUid in references.current) {
            if (!selected.hasOwnProperty(referenceUid)) {
                waitForSvgAndDecolorize(references, referenceUid);
            }
        }

    }, [references, chosenAccessories, chosenComplectations, complectations]);

    return {
        sections: [...sectionsComplectations, ...sectionsAccessoriesFiltered],
        references
    }
};


// --------------------------------------
// Helper local hooks
// --------------------------------------


const useSections = (blocks: ComplectationBlock[], selected: OptionsSelected = {}) => {
    return useMemo(() => {
        return blocks.reduce<PreviewSection[]>((total, block) => {
            const item = findBlockItemBySelected(block, selected) || block.items[0];

            const slots = block.slots;

            if (item && !slots) {
                Object.entries(item.attributes).forEach(([attributeKey, attribute]) => {
                    total.push({
                        uid: generateUid(block.id, attributeKey),
                        image: attribute.image,
                    });
                });
            }


            if (item && slots && slots.positions.length) {

                for (let index = 0; index < slots.positions.length; index++) {

                    Object.entries(item.attributes).forEach(([attributeKey, attribute]) => {
                        total.push({
                            uid: generateUid(block.id, attributeKey, String(index)),
                            image: {...attribute.image, position: slots.positions[index]},
                        });
                    }, {});

                }
            }

            return total;
        }, []);
    }, [blocks, selected]);
};
